/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { type Registry as Services, service } from '@ember/service';
import { subDays } from 'date-fns/subDays';
import PhClock from 'ember-phosphor-icons/components/ph-clock';
import PhCheckCircle from 'ember-phosphor-icons/components/ph-check-circle';
import PhXCircle from 'ember-phosphor-icons/components/ph-x-circle';
import { SecurityDepositStatus } from 'uplisting-frontend/models/security-deposit';
import { formatDate } from 'uplisting-frontend/helpers/date-format';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  securityDeposit: ModelRegistry['security-deposit'];
}

export interface BookingStatusSecurityDepositSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

const REMINDERS_SENT_THRESHOLD = 3;
export const RELEASED_AT_FORMAT = 'MMM do, yyyy @ h:mm a';

export default class UiBookingStatusSecurityDepositComponent extends Component<BookingStatusSecurityDepositSignature> {
  @service intl!: Services['intl'];

  @cached
  get securityDeposit(): ModelRegistry['security-deposit'] {
    return this.args.securityDeposit;
  }

  @cached
  get settleAction(): ModelRegistry['security-deposit-action'] | undefined {
    const action = this.securityDeposit.booking.securityDepositAction;

    return action?.isSettle ? action : undefined;
  }

  @cached
  get expiredBeforeCheckOut(): boolean {
    const { expiresAt, booking } = this.securityDeposit;

    return isBefore(expiresAt, booking.checkOutDate);
  }

  @cached
  get status():
    | SecurityDepositStatus
    | 'missing_payment_information'
    | 'released'
    | 'auto_released'
    | 'expired'
    | 'claimed'
    | 'partially_claimed' {
    const { status, booking, releasedAt } = this.securityDeposit;
    const { settleAction } = this;

    if (settleAction && settleAction.capturedAmount > 0) {
      return settleAction.releasedAmount > 0 ? 'partially_claimed' : 'claimed';
    }

    if (releasedAt) {
      return 'released';
    }

    if (this.securityDeposit.isExpired) {
      return this.expiredBeforeCheckOut ? 'expired' : 'auto_released';
    }

    const chargeTime = subDays(booking.checkInDate, 1);
    const needsPaymentInformation =
      status === SecurityDepositStatus.needsPaymentInformation;
    const lessThan24HoursToCheckIn = isAfter(new Date(), chargeTime);
    const remindersSent =
      this.securityDeposit.remindersSent >= REMINDERS_SENT_THRESHOLD;

    if (
      needsPaymentInformation &&
      (lessThan24HoursToCheckIn || remindersSent)
    ) {
      return 'missing_payment_information';
    }

    return status;
  }

  @cached
  get alertClass():
    | 'inline-alert-warning'
    | 'inline-alert-danger'
    | 'inline-alert-processing'
    | 'inline-alert-success' {
    switch (this.status) {
      case SecurityDepositStatus.needsPaymentInformation:
      case 'expired':
        return 'inline-alert-warning';
      case 'missing_payment_information':
      case SecurityDepositStatus.failing:
      case SecurityDepositStatus.failed:
        return 'inline-alert-danger';
      case SecurityDepositStatus.authorized:
      case SecurityDepositStatus.collected:
      case 'released':
      case 'auto_released':
      case 'claimed':
      case 'partially_claimed':
        return 'inline-alert-success';
      case SecurityDepositStatus.hasPaymentInformation:
      case SecurityDepositStatus.overridden:
        return 'inline-alert-processing';
    }
  }

  @cached
  get statusTime(): string {
    const { chargedAt, releasedAt } = this.securityDeposit;
    const { status } = this;
    const claimed = status === 'claimed' || status === 'partially_claimed';

    return formatDate(claimed ? chargedAt : releasedAt, RELEASED_AT_FORMAT);
  }

  @cached
  get expiresAt(): string {
    return formatDate(this.securityDeposit.expiresAt, RELEASED_AT_FORMAT);
  }

  @cached
  get claimedAmount(): string {
    return this.formatAmount(this.settleAction?.capturedAmount ?? 0);
  }

  @cached
  get releasedAmount(): string {
    return this.formatAmount(this.settleAction?.releasedAmount ?? 0);
  }

  @cached
  get stripeError(): string {
    const key =
      'action_bookings_summary.security_deposit_status.stripe_generic_error';

    return this.securityDeposit.stripeError ?? this.intl.t(key);
  }

  @cached
  get iconComponent():
    | typeof PhXCircle
    | typeof PhCheckCircle
    | typeof PhClock
    | undefined {
    switch (this.status) {
      case 'missing_payment_information':
      case SecurityDepositStatus.failing:
      case SecurityDepositStatus.failed:
        return PhXCircle;
      case SecurityDepositStatus.authorized:
      case SecurityDepositStatus.collected:
      case SecurityDepositStatus.hasPaymentInformation:
      case SecurityDepositStatus.overridden:
      case 'released':
      case 'auto_released':
      case 'claimed':
      case 'partially_claimed':
        return PhCheckCircle;
      case SecurityDepositStatus.needsPaymentInformation:
      case 'expired':
        return PhClock;
    }
  }

  private formatAmount(amount: number): string {
    return this.intl.formatNumber(amount, {
      style: 'currency',
      currency: this.securityDeposit.booking.currency,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Status::SecurityDeposit': typeof UiBookingStatusSecurityDepositComponent;
  }
}
